import * as React from 'react';
import {
    Box,
    Button,
    Dialog,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from '@mui/material';
import Filter from 'component/filter';
import colors from 'assets/theme/base/colors';
import borders from 'assets/theme/base/borders';
import { useEffect, useState } from 'react';
import { RowDataType, TableMetaData } from 'types/common/common';
import { useAppDispatch, useAppSelector } from 'hooks/redux';
import { ReactComponent as Edit } from 'assets/images/staff-user/edit.svg';
import { ReactComponent as Delete } from 'assets/images/staff-user/delete.svg';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import { openConfirmationDialogs } from 'store/slices/confirmation-dialog/confirmation-dialog-slice';
import AddImages from 'component/add-images';
import pxToRem from 'assets/theme/function/px-to-rem';
import { openSnackbar } from 'store/slices/snackbar/snackbar-slice';
import { ImageTypeEnum } from 'types/card/card';
import { StyledTableRow } from 'component/tabel-pagination-style';
import { API_ENDPOINT_URL } from 'constant';
import ViewPhoto from 'component/view-photo';
import { openBackdrop } from 'store/slices/backdrop/backdrop-slice';
import { useParams } from 'react-router-dom';
import AddCategory from 'pages/toon-tint/add-category-toon-tint';
import EditCategory from 'pages/toon-tint/edit-category-toon-tint';
import { ToolTintData } from 'types/toont-tint/toon-tint';
import rgba from 'assets/theme/function/rgba';
import { ReactComponent as Plus } from 'assets/images/plus.svg';
import dayjs from 'dayjs';
import ToonTintItem from 'pages/toon-tint/toon-tint-item';
import { useCategoryListQuery } from 'store/slices/toon-tint/toon-tint';
import { useCategoryImageUpdateMutation } from 'store/slices/toon-tint/toon-tint-add-category-images';
import {
    useCategoryDeleteMutation,
    useCategoryFileDeleteMutation,
} from 'store/slices/toon-tint/tool-tint-delete';
import {
    addNewCategoryId,
    selectCategoryIdOptions,
} from 'store/slices/toon-tint/toon-tint-category-id-slice';
import { selectNewCardDataSliceOptions } from 'store/slices/cards/card/card-data-slice';
import BackNavigate from 'component/back-navigate/back-navigate';
import FolderDeleteIcon from '@mui/icons-material/FolderDelete';

const defaultTableStaffDataList: TableMetaData[] = [
    {
        key: 'img',
        position: 1,
        title: 'Icon Category',
        isSortable: false,
    },

    {
        key: 'categoryPosition',
        position: 2,
        title: 'Position In Category',
        isSortable: true,
    },

    {
        key: 'itemZIndex',
        position: 3,
        title: 'Item Z_Index',
        isSortable: true,
    },

    {
        key: 'defaultSelected',
        position: 4,
        title: 'Default Selected',
        isSortable: false,
    },

    {
        key: 'isVisible',
        position: 5,
        title: 'Is Visible',
        isSortable: false,
    },

    {
        key: 'createdAt',
        position: 7,
        title: 'Created Data',
        isSortable: true,
        type: 'date',
        format: 'DD.MM.YYYY HH:mm',
    },

    {
        key: 'action',
        position: 8,
        title: 'Action',
        isSortable: false,
    },
];

export type UrlObject = {
    url?: string;
    title?: string;
};

const ToonTint: React.FC = () => {
    const [openAddCategoryDialog, setOpenAddCategoryDialog] =
        React.useState(false);
    const [openEditCategoryDialog, setOpenEditCategoryDialog] =
        React.useState(false);
    const [openImgDialog, setOpenImgDialog] = React.useState(false);
    const [dataCategory, setDataCategory] = React.useState<ToolTintData>();
    const [tableData, setTableData] = useState<RowDataType[]>([]);
    const [viewPhotoUrl, setViewPhotoUrl] = useState<UrlObject | null>(null);
    const [selectedId, setSelectedId] = useState<string | null>(null);

    const [
        categoryImagesUpdateApi,
        { isSuccess, isError: imageIsError, error: imageError },
    ] = useCategoryImageUpdateMutation();

    const { cardId } = useParams();

    const { data, isFetching, refetch } = useCategoryListQuery(cardId ?? '');

    const { categoryId } = useAppSelector(selectCategoryIdOptions);
    const { fileId, cardName, allowedGamesCard, code } = useAppSelector(
        selectNewCardDataSliceOptions,
    );

    const flatAllowedGamesCard = allowedGamesCard?.flat() ?? [];

    const params = new URLSearchParams(location.search);

    const [
        categoryDeleteApi,
        {
            error: deleteError,
            isError: deleteIsError,
            isSuccess: deleteIsSuccess,
        },
    ] = useCategoryDeleteMutation();

    const [
        categoryDeleteFileApi,
        {
            error: deleteFileError,
            isError: deleteFileIsError,
            isSuccess: deleteFileIsSuccess,
        },
    ] = useCategoryFileDeleteMutation();

    const dispatch = useAppDispatch();

    const createNewCategory = () => {
        setOpenAddCategoryDialog(true);
    };

    const updateImages = (images: FormData) => {
        categoryImagesUpdateApi({
            categoryId: dataCategory?.id,
            image: images,
        });
    };

    const deleteCategory = ({ id }: ToolTintData) => {
        if (id) {
            categoryDeleteApi(id);
        }
    };

    const deleteFile = ({ id }: ToolTintData) => {
        if (id) {
            categoryDeleteFileApi(id);
        }
    };

    const getCategoryListFunction = () => {
        refetch();
    };

    const handelClickEditCategoryPersonage = (categoryId: ToolTintData) => {
        setOpenEditCategoryDialog(true);
        setDataCategory(categoryId);
    };

    const openPictureDialogFun = (categoryId: ToolTintData) => {
        setDataCategory(categoryId);
        setOpenImgDialog(true);
    };

    useEffect(() => {
        const tableData: RowDataType[] = data
            ? data.data.map((fullInfo) => {
                  return {
                      ...fullInfo,
                      img: (
                          <>
                              {fullInfo?.fileId ? (
                                  <Box
                                      component="img"
                                      src={`${API_ENDPOINT_URL}/file/file/${fullInfo?.fileId}?source=${ImageTypeEnum.TOON_TINT_CATEGORY}`}
                                      alt=""
                                      onClick={() => {
                                          setViewPhotoUrl({
                                              url: `${API_ENDPOINT_URL}/file/file/${fullInfo?.fileId}?source=${ImageTypeEnum.TOON_TINT_CATEGORY}`,
                                          });
                                      }}
                                      sx={{
                                          cursor: 'pointer',
                                          width: 30,
                                          height: 30,
                                          objectFit: 'contain',
                                          objectPosition: 'top',
                                          borderRadius: borders.borderRadius.md,
                                      }}
                                  />
                              ) : (
                                  <Typography variant={'h5'}>-</Typography>
                              )}
                          </>
                      ),

                      action: flatAllowedGamesCard.includes('toon-tint') ? (
                          <Box />
                      ) : (
                          <Box
                              display="flex"
                              justifyContent="center"
                              gap={2}
                              onClick={(e) => e.stopPropagation()}
                          >
                              <Tooltip title="Delete File">
                                  <FolderDeleteIcon
                                      cursor="pointer"
                                      onClick={(e) => {
                                          e.stopPropagation();
                                          dispatch(
                                              openConfirmationDialogs({
                                                  textInfo:
                                                      'Are You Sure Want To Delete File',
                                                  handleConfirm: deleteFile,
                                                  parameters: {
                                                      id: fullInfo.id,
                                                  },
                                              }),
                                          );
                                      }}
                                  />
                              </Tooltip>

                              <Tooltip title="Delete">
                                  <Delete
                                      cursor="pointer"
                                      onClick={(e) => {
                                          e.stopPropagation();
                                          dispatch(
                                              openConfirmationDialogs({
                                                  textInfo:
                                                      'Are You Sure Want To Delete Category',
                                                  handleConfirm: deleteCategory,
                                                  parameters: {
                                                      id: fullInfo.id,
                                                  },
                                              }),
                                          );
                                      }}
                                  />
                              </Tooltip>

                              <Tooltip title="Edit">
                                  <Edit
                                      cursor="pointer"
                                      onClick={(e) => {
                                          e.stopPropagation();
                                          handelClickEditCategoryPersonage({
                                              fileId: fullInfo.id,
                                              id: fullInfo.id,
                                          });
                                      }}
                                  />
                              </Tooltip>

                              <Tooltip title="Add photo">
                                  <AddPhotoAlternateIcon
                                      titleAccess="Add photo"
                                      cursor="pointer"
                                      onClick={(e) => {
                                          e.stopPropagation();
                                          openPictureDialogFun({
                                              fileId: fullInfo.fileId,
                                              id: fullInfo.id,
                                              imageType:
                                                  ImageTypeEnum.TOON_TINT_CATEGORY,
                                          });
                                      }}
                                  />
                              </Tooltip>
                          </Box>
                      ),
                      rowComponent: StyledTableRow,
                  };
              })
            : [];
        setTableData(tableData);
    }, [data]);

    useEffect(() => {
        if (deleteIsSuccess || deleteFileIsSuccess || isSuccess) {
            setOpenImgDialog(false);
            getCategoryListFunction();
        }
    }, [deleteIsSuccess, isSuccess, deleteFileIsSuccess]);

    useEffect(() => {
        dispatch(openBackdrop({ open: isFetching }));
    }, [isFetching]);

    useEffect(() => {
        const error = deleteError || deleteFileError || imageError;

        if (error && 'data' in error && error.data?.message) {
            dispatch(
                openSnackbar({
                    message: error.data.message,
                    severity: 'error',
                    open: true,
                    position: {
                        vertical: 'top',
                        horizontal: 'right',
                    },
                }),
            );
        }
    }, [deleteIsError, deleteFileIsError, imageIsError]);

    return (
        <>
            <Box mb={pxToRem(5)} display="grid" alignItems="center">
                <BackNavigate navigateUrl={`/card?${params.toString()}`} />
            </Box>
            <Box
                sx={{
                    width: '100%',
                }}
            >
                <Filter name={'Toon Tint Builder'} display={true} />
                <Box
                    display={'flex'}
                    alignItems={'center'}
                    width={'20%'}
                    border={`1px solid ${rgba(colors.info.main, 0.5)}`}
                    borderRadius={borders.borderRadius.md}
                    p={pxToRem(8)}
                    pb={pxToRem(15)}
                >
                    {cardId && fileId ? (
                        <Box
                            component="img"
                            src={`${API_ENDPOINT_URL}/file/file/${fileId}?source=${ImageTypeEnum.CARD}`}
                            alt=""
                            onClick={() => {
                                setViewPhotoUrl({
                                    url: `${API_ENDPOINT_URL}/file/file/${fileId}?source=${ImageTypeEnum.CARD}`,
                                });
                            }}
                            sx={{
                                cursor: 'pointer',
                                width: 100,
                                height: 80,
                                objectFit: 'contain',
                                objectPosition: 'top',
                                borderRadius: borders.borderRadius.md,
                            }}
                        />
                    ) : (
                        <Typography variant={'h5'}>-</Typography>
                    )}
                    <Box>
                        <Typography
                            pl={pxToRem(10)}
                            color={rgba(colors.primary.main, 1)}
                            variant={'subtitle1'}
                        >
                            {cardName}
                        </Typography>
                        <Typography
                            pl={pxToRem(10)}
                            pt={pxToRem(10)}
                            color={rgba(colors.primary.main, 1)}
                            variant={'subtitle1'}
                        >
                            {code}
                        </Typography>
                    </Box>
                </Box>

                <Box
                    bgcolor={colors.secondary.light}
                    borderRadius={borders.borderRadius.md}
                    mt={pxToRem(7)}
                    pb={pxToRem(20)}
                >
                    {flatAllowedGamesCard.includes('toon-tint') ? (
                        <Box />
                    ) : (
                        <Box
                            display={'flex'}
                            justifyContent={'end'}
                            alignItems={'center'}
                        >
                            <Button onClick={createNewCategory} size={'small'}>
                                <Box display={'flex'} alignItems={'center'}>
                                    <Plus />
                                    <Typography
                                        pl={pxToRem(10)}
                                        color={rgba(colors.primary.main, 1)}
                                        variant={'subtitle1'}
                                    >
                                        Creat New Category
                                    </Typography>
                                </Box>
                            </Button>
                        </Box>
                    )}

                    <TableContainer
                        sx={{
                            minHeight: pxToRem(150),
                            maxHeight: pxToRem(300),
                            overflowY: 'auto',
                        }}
                    >
                        <Table>
                            <TableHead>
                                <TableRow>
                                    {defaultTableStaffDataList.map((col) => (
                                        <TableCell key={col.key}>
                                            {col.title}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {tableData.map((row, idx) => (
                                    <StyledTableRow
                                        key={idx}
                                        data={row}
                                        selected={row.id === selectedId}
                                        sx={{
                                            cursor: 'pointer',
                                            '&.Mui-selected': {
                                                backgroundColor:
                                                    colors.action.progress,
                                            },
                                        }}
                                        onClick={() => {
                                            setSelectedId(row.id);
                                            setDataCategory(
                                                (prev) =>
                                                    ({
                                                        ...prev,
                                                    }) as ToolTintData,
                                            );
                                            dispatch(
                                                addNewCategoryId({
                                                    categoryId: row.id,
                                                }),
                                            );
                                        }}
                                    >
                                        {defaultTableStaffDataList.map(
                                            (col) => {
                                                const value = row[col.key];

                                                let displayValue:
                                                    | string
                                                    | number
                                                    | null = value;

                                                if (
                                                    col.key === 'createdAt' &&
                                                    value
                                                ) {
                                                    displayValue =
                                                        dayjs(value).format(
                                                            'DD.MM.YYYY',
                                                        );
                                                } else if (
                                                    typeof value === 'boolean'
                                                ) {
                                                    displayValue = value
                                                        ? 'true'
                                                        : 'false';
                                                } else if (value == null) {
                                                    displayValue = '-';
                                                }

                                                return (
                                                    <TableCell key={col.key}>
                                                        {displayValue}
                                                    </TableCell>
                                                );
                                            },
                                        )}
                                    </StyledTableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>

                {categoryId !== '' && <ToonTintItem />}

                <>
                    <Dialog
                        open={openAddCategoryDialog}
                        onClose={() => {
                            setOpenAddCategoryDialog(false);
                        }}
                    >
                        <AddCategory
                            refetch={getCategoryListFunction}
                            setOpenDialog={setOpenAddCategoryDialog}
                            cardId={cardId}
                        />
                    </Dialog>

                    <Dialog
                        open={openImgDialog}
                        onClose={() => {
                            setOpenImgDialog(false);
                        }}
                    >
                        <AddImages
                            imageUrl={[
                                dataCategory?.fileId
                                    ? `${API_ENDPOINT_URL}/file/file/${dataCategory.fileId}?source=${dataCategory.imageType}`
                                    : undefined,
                            ]}
                            setOpenImgDialog={setOpenImgDialog}
                            onSubmitFun={updateImages}
                            dialogTitle={'Category IMAGE'}
                        />
                    </Dialog>

                    <Dialog
                        open={openEditCategoryDialog}
                        onClose={() => {
                            setOpenEditCategoryDialog(false);
                        }}
                    >
                        <EditCategory
                            refetch={refetch}
                            categoryId={dataCategory?.id ?? ''}
                            setOpenEditCategoryDialog={
                                setOpenEditCategoryDialog
                            }
                        />
                    </Dialog>
                </>

                {viewPhotoUrl && (
                    <ViewPhoto
                        open={Boolean(viewPhotoUrl)}
                        setOpen={() => setViewPhotoUrl(null)}
                        imageUrl={viewPhotoUrl}
                    />
                )}
            </Box>
        </>
    );
};

export default ToonTint;
